// src/components/Frontend/RevenueTeardown/VisualCard/VisualTile.jsx
import React, { useState, useRef, useMemo } from "react";
import Donut from "./Donut";
import DrillOptionsCard from "./DrillOptionsCard";  // <-- use the anchored popover
import { RowIcons } from "../MetricPills";
const PALETTE = ["#4C6FFF","#2DBE7B","#F6B73A","#EE5D50","#8A6DF1","#32B9C6","#A0A0A0","#FF8BD2"];

function fmtPct(p) {
if (p == null || isNaN(p)) return "";
  const n = Number(p);              // already a percent
  return `${n.toFixed(1)}%`;
}

// Always show one decimal and a space before %
function GrowthPillStrict({ pct }) {
 if (pct == null || isNaN(pct)) return null;
  const n = Number(pct);     
  const absStr = Math.abs(n).toFixed(1); // e.g., "25.0"
  const text = `${n >= 0 ? "+" : ""}${absStr} %`; // "+25.0 %"

  let cls = "growth-pill growth-blue";
  if (n > 15) cls = "growth-pill growth-green";
  else if (n >= 10) cls = "growth-pill growth-blue";
  else if (n > 0) cls = "growth-pill growth-orange";
  else cls = "growth-pill growth-red";

  return <span className={cls}>{text}</span>;
}


export default function VisualTile({
  title,
  centerValue,
  items = [],
  onDrill,                 // will be called after user picks a next dim
  onAIClick,  
  showClose = false,
  onClose             // opens your AI drawer
}) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const [chooserFor, setChooserFor] = useState(null);   // active row for the popover
  const tileRef = useRef(null);

  const selectedRow = useMemo(
    () => (hoverIdx != null ? items[hoverIdx] : null),
    [hoverIdx, items]
  );

  // map keys -> labels/subtext/icons for the chooser
  const labelForKey = (k) =>
    ({
      business: "Business/Customer Segments",
      product: "Product/Service Mix",
      customer: "Customer Segments",
      application: "Application Markets",
      channel: "Sales Channels",
      geo: "Geography",
    }[k] || k);

  const subForKey = (k) =>
    ({
      business: "Break down by business units and segments",
      product: "View revenue by products and service categories",
      customer: "Analyze customer types and specific accounts",
      application: "View by end-use applications and markets",
      channel: "Breakdown by distribution and sales channels",
      geo: "Analyze revenue by geographic regions and markets",
    }[k] || "Choose this dimension");

  const iconForKey = (k) =>
    ({
      business: "ri-briefcase-2-line",
      product: "ri-price-tag-3-line",
      customer: "ri-user-3-line",
      application: "ri-apps-2-line",
      channel: "ri-node-tree",
      geo: "ri-map-pin-2-line",
    }[k] || "ri-grid-line");

  return (
    <div className="rt-visual-tile" ref={tileRef}>
      <div className="tile-head">
        <h5>{title}</h5>
        <div className="tile-sub">Total: {centerValue} • {items.length} {title.toLowerCase()}
             {showClose && (
          <button className="tile-close tile-close-lg" aria-label="Close card" onClick={onClose}>
           <i className="ri-close-line" />
         </button>
        )}
        </div>
      </div>

      <div className="tile-donuts">
        <Donut data={items} centerValue={centerValue} />
      </div>

      <div className="legend legend-scroll">
        {items.map((r, i) => {
          const canDrill = !!r.breakdowns && Object.keys(r.breakdowns).length > 0;
          return (
            <div
              key={r.label + i}
              className="opt-card"
              onMouseEnter={() => setHoverIdx(i)}
              onClick={() => {
                const canDrill = !!r.breakdowns && Object.keys(r.breakdowns).length > 0;
                if (canDrill) setChooserFor(r);
              }}
            >
              {/* left: dot + label + share */}
              <div className="opt-left">
                <span className="opt-dot" style={{ background: PALETTE[i % PALETTE.length] }} />
                <div className="opt-text">
                  <div className="opt-label">{r.label}</div>
                  <div className="opt-sub">{fmtPct(r.sharePct)}</div>
                </div>
              </div>

              {/* right: value + growth */}
              <div className="opt-right">
                <div className="opt-value">{r.value}</div>
                <GrowthPillStrict pct={r.growthPct} />
              </div>

              {/* icons: info/AI/chevron */}
              <div className="opt-icons">
                {/* info (optional) */}
               <RowIcons
                  showAI={true}
                  onAIClick={() => onAIClick?.(r)}
                  sources={r.sources}
                  baseFacts={r.baseFacts}
                  nodeLabel={r.label}
                />

                {/* chevron (opens floating chooser) */}
                {canDrill && (
                  <i
                    className="ri-arrow-right-s-line opt-chevron"
                    title="Drill down"
                    onClick={(e) => {
                      e.stopPropagation();
                      setChooserFor(r);     // <-- open popover for this row
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* anchored popover (Figma style) */}
      {chooserFor && (
        <DrillOptionsCard
          anchorRef={tileRef}
          rowLabel={chooserFor.label}
          options={Object.keys(chooserFor.breakdowns || {}).map((k) => ({
            key: k,
            label: labelForKey(k),
            sub: subForKey(k),
            icon: iconForKey(k),
          }))}
          onSelect={(dimKey) => {
            onDrill?.(chooserFor, dimKey);   // let parent append the next tile
            setChooserFor(null);
          }}
          onClose={() => setChooserFor(null)}
        />
      )}
    </div>
  );
}
